import { ComboBox } from 'src/components/ui/combobox';
import { LABEL_TEXT_RESULT_OF_TABLE } from 'src/localization/resouceKeys';
import { useLocalization } from 'src/utils/useLocalization';
import { useTranslation } from 'react-i18next';
import React, { Dispatch, SetStateAction, useEffect, useState } from 'react';

import './style.scss';

export const TableFooter: React.FC<{
    content: Array<any>;
    brand: string;
    setDisplayedContent: Dispatch<SetStateAction<Array<any>>>;
    defaultPerPage?: number;
    perPageOptions?: string[];
}> = ({
    content,
    brand,
    setDisplayedContent,
    defaultPerPage = 25,
    perPageOptions = ['10', '25', '50', '100'],
}) => {
    const { t } = useTranslation();
    const { labelText } = useLocalization();
    const MAX_BEFORE_PAGINATION = 11;
    const defaultPage = 1;
    const [contentPage, setContentPage] = useState(defaultPage);
    const [contentSlice, setContentSlice] = useState(defaultPerPage);
    const defaultStartIndex = 0;
    const startIndex =
        content.length < contentSlice
            ? defaultStartIndex
            : contentSlice * contentPage - contentSlice;
    const endIndex = contentPage * contentSlice;
    const paginatedContent = content.slice(startIndex, endIndex);
    const numberOfPages = Math.ceil(content.length / contentSlice);
    const showPaginationUp: boolean = contentPage < numberOfPages;
    const showPaginationDown: boolean =
        !!content.length && contentPage > defaultPage;
    const numberShown = `${startIndex + defaultPage}-${
        endIndex < content.length ? endIndex : content.length
    }`;

    useEffect(() => {
        if (!!numberOfPages && contentPage > numberOfPages) {
            setContentPage(numberOfPages);
        }
        setDisplayedContent(paginatedContent);
    }, [content, contentPage, contentSlice]);

    const PageIndicator: React.FC<{
        key: number;
        index: number;
        shouldHide: boolean;
    }> = ({ index, shouldHide }) => {
        const hiddenClass = shouldHide ? 'hidden' : '';
        return (
            <div
                className={`pagination ${
                    contentPage === index
                        ? `pagination-selected pagination-selected-${brand}`
                        : ''
                } ${hiddenClass}`}
                onClick={() => {
                    if (contentPage !== index) {
                        setContentPage(index);
                    }
                }}
            >
                <div>{index}</div>
            </div>
        );
    };

    const Pagination = () => {
        const pageIndicators = [];
        const startingIndex = 1;
        function shouldHide(ind: number) {
            const CONTAINER_TO_HIDE = 2;
            if (
                ind === startingIndex ||
                ind === numberOfPages ||
                (contentPage + CONTAINER_TO_HIDE > numberOfPages &&
                    ind >=
                        numberOfPages -
                            CONTAINER_TO_HIDE -
                            CONTAINER_TO_HIDE) ||
                (contentPage - CONTAINER_TO_HIDE < startingIndex &&
                    ind <=
                        startingIndex + CONTAINER_TO_HIDE + CONTAINER_TO_HIDE)
            ) {
                return false;
            } else if (
                ind > contentPage + CONTAINER_TO_HIDE ||
                ind < contentPage - CONTAINER_TO_HIDE
            ) {
                return true;
            } else return false;
        }
        for (let i = startingIndex; i < numberOfPages + startingIndex; i++) {
            pageIndicators.push(
                <PageIndicator key={i} index={i} shouldHide={shouldHide(i)} />,
            );
        }
        return pageIndicators;
    };

    return (
        <div className='after-table'>
            <div className='result-count'>
                <span className='hide-xs'>
                    {labelText.LABEL_TEXT_SHOWING}&nbsp;
                </span>
                <span>
                    {t(LABEL_TEXT_RESULT_OF_TABLE, {
                        numberShown,
                        totalLength: content.length,
                    })}
                </span>
            </div>
            <div
                className={`pagination-container ${
                    content.length <= contentSlice ? 'hidden' : ''
                }`}
            >
                {!!showPaginationDown ? (
                    <div
                        className='pagination-arrow'
                        onClick={() =>
                            setContentPage(contentPage - defaultPage)
                        }
                    >
                        <i
                            className={
                                'c-icon c-icon--[chevron-left] c-icon--small pagination-icon'
                            }
                        ></i>
                    </div>
                ) : null}
                {Pagination()}
                {!!showPaginationUp ? (
                    <div
                        className='pagination-arrow'
                        onClick={() =>
                            setContentPage(contentPage + defaultPage)
                        }
                    >
                        <i
                            className={
                                'c-icon c-icon--[chevron-right] c-icon--small pagination-icon'
                            }
                        ></i>
                    </div>
                ) : null}
            </div>
            <div
                className={`results-per-page c-input ${
                    content.length < MAX_BEFORE_PAGINATION ? 'hidden' : ''
                }`}
            >
                <span>{labelText.LABEL_TEXT_RESULT_PER_PAGE}</span>
                <div className='select-result-count'>
                    <ComboBox
                        name={'show-count'}
                        options={perPageOptions}
                        defaultValue={defaultPerPage.toString()}
                        selectedOption={contentSlice.toString()}
                        onSelect={setContentSlice}
                    />
                </div>
            </div>
        </div>
    );
};

export default TableFooter;
